/**
 * IK/FK debug logger — console.log only.
 *
 * All output is throttled to 1 log per second per category.
 * Zero overhead when debug mode is off.
 */

let _enabled = false;

export function setIKLogEnabled(on: boolean): void {
  _enabled = on;
}

export function isIKLogEnabled(): boolean {
  return _enabled;
}

// ---------------------------------------------------------------------------
// Throttle — one log per category per second
// ---------------------------------------------------------------------------

const _lastLog: Record<string, number> = {};
const THROTTLE_MS = 1000;

function throttled(category: string, msg: string): void {
  if (!_enabled) return;
  const now = Date.now();
  if (now - (_lastLog[category] ?? 0) < THROTTLE_MS) return;
  _lastLog[category] = now;
  console.log(`[ik] ${msg}`);
}

// Always log (no throttle) — for rare events
function immediate(msg: string): void {
  if (!_enabled) return;
  console.log(`[ik] ${msg}`);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deg(rad: number): string {
  return (rad * (180 / Math.PI)).toFixed(1) + "°";
}

// ---------------------------------------------------------------------------
// Analytical IK
// ---------------------------------------------------------------------------

export function logAnalyticalSolve(
  boneName: string,
  rootToTargetDist: number,
  maxReach: number,
  rootAngleRad: number,
  midDepthDelta: number,
): void {
  const reachPct = ((rootToTargetDist / maxReach) * 100).toFixed(0);
  throttled(
    "analytical",
    `[analytical] ${boneName} reach=${reachPct}% rootAngle=${deg(rootAngleRad)} depthΔ=${midDepthDelta.toFixed(4)}`,
  );
}

export function logAnalyticalFullExtend(
  boneName: string,
  dist: number,
  maxReach: number,
): void {
  throttled(
    "analytical",
    `[analytical] ${boneName} FULL EXTEND dist=${dist.toFixed(4)} max=${maxReach.toFixed(4)}`,
  );
}

// ---------------------------------------------------------------------------
// Constraint corrections
// ---------------------------------------------------------------------------

export function logConstraintCorrection(
  boneName: string,
  constraintType: string,
  preAngle: number,
  postAngle: number,
): void {
  const delta = Math.abs(preAngle - postAngle);
  if (delta < 0.001) return;
  throttled(
    `constraint:${boneName}`,
    `[constraint] ${boneName} (${constraintType}) ${deg(preAngle)}→${deg(postAngle)} Δ=${deg(delta)}`,
  );
}

export function logConstraintViolation(
  totalDeviation: number,
  isViolating: boolean,
): void {
  const state = isViolating ? "FROZEN" : "ok";
  throttled(
    "violation",
    `[violation] deviation=${deg(totalDeviation)} ${state}`,
  );
}

// ---------------------------------------------------------------------------
// Pointer vs joint delta — throttled to 1s
// ---------------------------------------------------------------------------

export function logPointerVsJoint(
  pointerDx: number,
  pointerDy: number,
  jointWorldDx: number,
  jointWorldDy: number,
  jointWorldDz: number,
  solverType: string,
): void {
  const pointerDist = Math.sqrt(pointerDx * pointerDx + pointerDy * pointerDy);
  const jointDist = Math.sqrt(
    jointWorldDx * jointWorldDx +
      jointWorldDy * jointWorldDy +
      jointWorldDz * jointWorldDz,
  );
  const ratio =
    pointerDist > 0.0001 ? (jointDist / pointerDist).toFixed(2) : "∞";
  const depthPct =
    jointDist > 0.0001
      ? ((Math.abs(jointWorldDz) / jointDist) * 100).toFixed(0)
      : "0";

  throttled(
    "pointer",
    `[pointer] Δptr=(${pointerDx.toFixed(1)},${pointerDy.toFixed(1)})px Δjoint=(${jointWorldDx.toFixed(4)},${jointWorldDy.toFixed(4)},${jointWorldDz.toFixed(4)}) ratio=${ratio} depth%=${depthPct} (${solverType})`,
  );
}

// ---------------------------------------------------------------------------
// Smoothing
// ---------------------------------------------------------------------------

export function logSmoothingClamp(
  boneName: string,
  rawAngle: number,
  budget: number,
): void {
  if (rawAngle <= budget) return;
  throttled(
    `smooth:${boneName}`,
    `[smooth] ${boneName} clamped ${deg(rawAngle)}→${deg(budget)}`,
  );
}

// ---------------------------------------------------------------------------
// Anti-parallel guard (rare — always log)
// ---------------------------------------------------------------------------

export function logAntiParallel(boneName: string, dot: number): void {
  immediate(
    `[anti-parallel] ${boneName} dot=${dot.toFixed(6)} → stable 180° rotation`,
  );
}

// ---------------------------------------------------------------------------
// Whole-body IK — dispatch, iteration, and joint deltas
// ---------------------------------------------------------------------------

/**
 * Log when the whole-body solver is dispatched.
 * Shows target names, skeleton bone count, and DOF count.
 */
export function logWholeBodyDispatch(
  targetNames: string[],
  boneCount: number,
  dofCount: number,
): void {
  throttled(
    "wb-dispatch",
    `[whole-body] dispatch: targets=[${targetNames.join(", ")}] ` +
      `bones=${boneCount} dofs=${dofCount}`,
  );
}

/**
 * Log per-iteration convergence: error per target.
 * Throttled — only the last iteration's snapshot logs each second.
 */
let _wbIterBuf: string | null = null;

export function logWholeBodyIteration(
  iter: number,
  targetErrors: { name: string; error: number }[],
  maxError: number,
  converged: boolean,
): void {
  if (!_enabled) return;
  const errs = targetErrors
    .map((t) => `${t.name}=${t.error.toFixed(4)}`)
    .join(" ");
  _wbIterBuf = `[whole-body] iter=${iter} maxErr=${maxError.toFixed(4)} ${converged ? "CONVERGED" : errs}`;
}

/** Flush the buffered iteration log (call once per solve). */
export function flushWholeBodyIteration(): void {
  if (!_wbIterBuf) return;
  throttled("wb-iter", _wbIterBuf);
  _wbIterBuf = null;
}

/**
 * Log which joints actually changed and by how much (degrees).
 * Only logs joints whose rotation changed > 0.1° from the starting pose.
 * Deduped: identical joint sets within the throttle window are skipped.
 */
let _prevJointSummary = "";

export function logWholeBodyResult(
  jointDeltas: { name: string; angleDeg: number }[],
): void {
  if (!_enabled) return;
  // Filter to meaningful changes
  const changed = jointDeltas.filter((j) => j.angleDeg > 0.1);
  if (changed.length === 0) {
    throttled("wb-result", "[whole-body] result: no joints changed > 0.1°");
    return;
  }
  // Sort by magnitude, largest first
  changed.sort((a, b) => b.angleDeg - a.angleDeg);
  const summary = changed
    .map((j) => `${j.name} ${j.angleDeg.toFixed(1)}°`)
    .join("  ");
  // Dedupe identical summaries
  if (summary === _prevJointSummary) return;
  _prevJointSummary = summary;
  throttled("wb-result", `[whole-body] result: ${summary}`);
}

/**
 * Log target positions and bone world positions for diagnosis.
 */
export function logWholeBodyTargets(
  targets: {
    name: string;
    target: [number, number, number];
    actual: [number, number, number];
  }[],
): void {
  if (!_enabled) return;
  const lines = targets.map((t) => {
    const dx = t.target[0] - t.actual[0];
    const dy = t.target[1] - t.actual[1];
    const dz = t.target[2] - t.actual[2];
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return `  ${t.name}: target=(${t.target[0].toFixed(3)},${t.target[1].toFixed(3)},${t.target[2].toFixed(3)}) actual=(${t.actual[0].toFixed(3)},${t.actual[1].toFixed(3)},${t.actual[2].toFixed(3)}) err=${dist.toFixed(4)}`;
  });
  throttled("wb-targets", `[whole-body] targets:\n${lines.join("\n")}`);
}

/**
 * Log what gets applied to the store after the solve.
 * Shows the dragged bone, number of bones applied, and warm-start state.
 */
export function logWholeBodyApply(
  activeBone: string,
  solvedQuats: ReadonlyMap<string, [number, number, number, number]>,
  isFirstFrame: boolean,
): void {
  if (!_enabled) return;
  const hasDragged = solvedQuats.has(activeBone);
  throttled(
    "wb-apply",
    `[whole-body] apply: drag=${activeBone}(${hasDragged ? "included" : "MISSING"}) ` +
      `bones=${solvedQuats.size} warmStart=${!isFirstFrame}`,
  );
}
